import React, { useState, useEffect, useRef } from 'react';
import db from '../../utils/database';
import { useNotification } from '../../context/NotificationContext';
import { handleDatabaseError } from '../../utils/errorHandler';
import { LoadingButton } from '../../components/common/LoadingIndicator';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const SqlRunner = ({ password }) => {
  const notification = useNotification();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [affectedRows, setAffectedRows] = useState(0);
  const [executionTime, setExecutionTime] = useState(0);
  const [editorHeight, setEditorHeight] = useState(150);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDangerousQuery, setPendingDangerousQuery] = useState('');
  const textareaRef = useRef(null);
  
  // Load query history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('sql_query_history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Failed to parse query history from localStorage');
      }
    }
  }, []);
  
  // Save query history to localStorage
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('sql_query_history', JSON.stringify(history.slice(0, 20)));
    }
  }, [history]);
  
  // Enable SQL syntax highlighting with the textarea
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      
      // Very basic SQL syntax highlighting for a textarea
      const highlightSql = () => {
        const value = textarea.value;

        // Create a temporary div to hold the highlighted content
        const highlightedDiv = document.getElementById('sql-highlight');
        if (highlightedDiv) {
          // Simple regex patterns for SQL keywords and syntax
          const sqlKeywords = /\b(SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|CROSS|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET|UNION|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|TABLE|INDEX|VIEW|TRIGGER|CASCADE|REFERENCES|CONSTRAINT|PRIMARY KEY|FOREIGN KEY|NOT NULL|UNIQUE|DEFAULT|AS|ON|IS|NULL|AND|OR|IN|BETWEEN|LIKE|CASE|WHEN|THEN|ELSE|END|ASC|DESC|DISTINCT)\b/gi;
          const sqlStrings = /'([^'\\]*(\\.[^'\\]*)*)'|"([^"\\]*(\\.[^"\\]*)*)"/g;
          const sqlNumbers = /\b\d+\b/g;
          const sqlComments = /--.*$/gm;

          // Apply highlighting
          let html = value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(sqlKeywords, (match) => `<span class="text-blue-600">${match}</span>`)
            .replace(sqlStrings, (match) => `<span class="text-green-600">${match}</span>`)
            .replace(sqlNumbers, (match) => `<span class="text-amber-600">${match}</span>`)
            .replace(sqlComments, (match) => `<span class="text-gray-500">${match}</span>`);

          // Ensure line breaks are preserved
          html = html.replace(/\n/g, '<br>');

          highlightedDiv.innerHTML = html || '&nbsp;';
        }
      };

      highlightSql();

      // Update highlighting when query changes
      textarea.addEventListener('input', highlightSql);
      textarea.addEventListener('scroll', () => {
        const highlightDiv = document.getElementById('sql-highlight');
        if (highlightDiv) {
          highlightDiv.scrollTop = textarea.scrollTop;
        }
      });
      
      return () => {
        textarea.removeEventListener('input', highlightSql);
      };
    }
  }, []);
  
  // Check if query is potentially destructive
  const isDangerousQuery = (sql) => {
    const upperQuery = sql.trim().toUpperCase();
    return (
      upperQuery.startsWith('DELETE') ||
      upperQuery.startsWith('DROP') ||
      upperQuery.startsWith('TRUNCATE') ||
      upperQuery.startsWith('ALTER') ||
      (upperQuery.startsWith('UPDATE') && !upperQuery.includes('WHERE'))
    );
  };

  // Prepare to execute query - with confirmation for dangerous queries
  const prepareQuery = () => {
    if (!query.trim()) {
      setError('Please enter a SQL query');
      notification.showWarning('Please enter a SQL query');
      return;
    }

    // Check if it's a potentially destructive query
    if (isDangerousQuery(query)) {
      setPendingDangerousQuery(query);
      setShowConfirmDialog(true);
    } else {
      // Safe query, execute immediately
      executeQuery(query);
    }
  };

  // Execute the SQL query
  const executeQuery = async (sqlQuery = query) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setAffectedRows(0);
    setShowConfirmDialog(false);

    try {
      const startTime = performance.now();

      // Execute the query
      const result = await db.query(sqlQuery);

      const endTime = performance.now();
      setExecutionTime(endTime - startTime);

      // Add query to history if it's not a duplicate of the most recent one
      if (history.length === 0 || history[0] !== sqlQuery) {
        setHistory([sqlQuery, ...history.slice(0, 19)]);
      }

      // Handle different query types
      if (sqlQuery.trim().toUpperCase().startsWith('SELECT')) {
        setResults(result);
        setAffectedRows(result.rows.length);
        notification.showSuccess(`Query returned ${result.rows.length} rows in ${(endTime - startTime).toFixed(2)}ms`);
      } else {
        // For non-SELECT queries (INSERT, UPDATE, DELETE, etc.)
        setAffectedRows(result.rowsAffected || 0);
        setResults({
          columns: ['Message'],
          rows: [{ Message: `Query executed successfully. ${result.rowsAffected || 0} rows affected.` }]
        });
        notification.showSuccess(`Query executed successfully. ${result.rowsAffected || 0} rows affected.`);
      }
    } catch (err) {
      const errorMessage = handleDatabaseError(err, 'query execution');
      setError(errorMessage);
      notification.showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Clear the query and results
  const clearQuery = () => {
    setQuery('');
    setResults(null);
    setError(null);
  };
  
  // Select a query from history
  const selectFromHistory = (historyQuery) => {
    setQuery(historyQuery);
    setShowHistory(false);
  };
  
  // Adjust editor height
  const increaseHeight = () => {
    setEditorHeight(Math.min(editorHeight + 50, 500));
  };
  
  const decreaseHeight = () => {
    setEditorHeight(Math.max(editorHeight - 50, 100));
  };
  
  // Format query with basic formatting
  const formatQuery = () => {
    if (!query.trim()) return;
    
    // Simple SQL formatter
    let formatted = query
      // Replace multiple spaces with a single space
      .replace(/\s+/g, ' ')
      // Add line breaks after specific keywords
      .replace(/\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET|JOIN|UNION|INSERT INTO|UPDATE|DELETE FROM|CREATE TABLE|ALTER TABLE|DROP TABLE)\b/gi, '\n$1')
      // Add line breaks and indentation for AND/OR
      .replace(/\b(AND|OR)\b/gi, '\n  $1')
      // Add indentation after certain keywords
      .replace(/\n(FROM|WHERE|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET)\b/gi, '\n$1')
      // Add commas at the end of the line
      .replace(/,\s*/g, ',\n  ')
      // Clean up excessive line breaks
      .replace(/\n\s*\n/g, '\n');
    
    setQuery(formatted);
  };
  
  // Keyboard shortcut for executing query (Ctrl+Enter)
  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      prepareQuery();
      e.preventDefault();
    }
  };
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          SQL Query Runner
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Write and execute SQL queries directly on the database.
        </p>
      </div>
      
      <div className="p-4">
        {/* Query editor toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <LoadingButton
              onClick={prepareQuery}
              isLoading={loading}
              loadingText="Executing..."
              disabled={!query.trim()}
              className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center"
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Run Query
            </LoadingButton>
            
            <button
              onClick={clearQuery}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Clear
            </button>
            
            <button
              onClick={formatQuery}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Format
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500 hidden sm:block">
              Keyboard shortcut: Ctrl+Enter to run query
            </div>
            
            <div className="flex">
              <button
                onClick={decreaseHeight}
                className="px-2 py-1 bg-gray-200 text-gray-700 rounded-l-md hover:bg-gray-300 focus:outline-none"
                title="Decrease editor height"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <button
                onClick={increaseHeight}
                className="px-2 py-1 bg-gray-200 text-gray-700 rounded-r-md hover:bg-gray-300 focus:outline-none"
                title="Increase editor height"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                </svg>
              </button>
            </div>
            
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              History
            </button>
          </div>
        </div>
        
        {/* SQL editor with syntax highlighting */}
        <div className="relative border rounded-md overflow-hidden mb-4" style={{ height: `${editorHeight}px` }}>
          <div
            id="sql-highlight"
            className="absolute inset-0 font-mono text-sm p-2 whitespace-pre-wrap pointer-events-none overflow-auto"
            style={{ 
              backgroundColor: 'transparent',
              color: 'transparent'
            }}
          ></div>
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter your SQL query here..."
            className="absolute inset-0 w-full h-full resize-none font-mono text-sm p-2 bg-transparent focus:outline-none focus:ring-1 focus:ring-indigo-500 caret-gray-900"
            style={{ caretColor: 'black' }}
          ></textarea>
        </div>
        
        {/* Query history dropdown */}
        {showHistory && (
          <div className="border rounded-md bg-white shadow-lg mb-4 max-h-60 overflow-y-auto">
            <div className="p-2 bg-gray-50 border-b border-gray-200 font-medium text-gray-700">
              Query History
            </div>
            {history.length === 0 ? (
              <div className="p-4 text-gray-500 text-sm">No query history yet</div>
            ) : (
              <ul>
                {history.map((historyQuery, index) => (
                  <li
                    key={index}
                    className="border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50"
                  >
                    <button
                      onClick={() => selectFromHistory(historyQuery)}
                      className="w-full text-left p-2 text-sm text-gray-700 overflow-hidden overflow-ellipsis whitespace-nowrap"
                      title={historyQuery}
                    >
                      {historyQuery.length > 100 ? historyQuery.slice(0, 100) + '...' : historyQuery}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        )}
        
        {/* Query results */}
        {results && (
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 p-3 border-b border-gray-200 flex justify-between items-center">
              <h4 className="font-medium text-gray-700">Results</h4>
              <div className="text-sm text-gray-500">
                {affectedRows} row{affectedRows !== 1 ? 's' : ''} • {executionTime.toFixed(2)}ms
              </div>
            </div>
            
            <div className="overflow-x-auto">
              {results.rows && results.rows.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(results.rows[0]).map((column) => (
                        <th
                          key={column}
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {results.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {Object.entries(row).map(([column, value], colIndex) => (
                          <td key={`${rowIndex}-${colIndex}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {value === null ? (
                              <span className="text-gray-400 italic">NULL</span>
                            ) : typeof value === 'object' ? (
                              JSON.stringify(value)
                            ) : (
                              String(value)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {loading ? 'Executing query...' : 'No results to display'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation dialog for dangerous queries */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirm Destructive Query"
        message={
          <div>
            <p>You are about to execute a potentially destructive query that may modify or delete data:</p>
            <pre className="mt-2 bg-gray-100 p-2 rounded text-xs overflow-auto max-h-32">
              {pendingDangerousQuery}
            </pre>
            <p className="mt-2 font-semibold text-red-600">Are you sure you want to proceed?</p>
          </div>
        }
        confirmText="Execute Query"
        cancelText="Cancel"
        onConfirm={() => executeQuery(pendingDangerousQuery)}
        onCancel={() => setShowConfirmDialog(false)}
        type="danger"
      />
    </div>
  );
};

export default SqlRunner;